import React from 'react';
import { Link } from 'react-router-dom';
import { LINKEDIN_COMPANY } from '../seo/jsonLd';
import { CONTACT_EMAIL } from '../site';

const logoUrl = "/logo.svg";
// 44px rows are a thumb requirement, not a layout one: gate them on a coarse
// pointer so a mouse-driven footer keeps its compact rhythm.
const LIST_CLASS = "mt-4 space-y-3 [@media(pointer:coarse)]:mt-2 [@media(pointer:coarse)]:space-y-1";
const LINK_CLASS = "inline-flex items-center text-base text-gray-400 transition-colors duration-150 hover:text-accent-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 [@media(pointer:coarse)]:min-h-[44px] [@media(pointer:coarse)]:py-2";

// Every entry is a URL. The column used to be five buttons opening modals, which
// meant the footer did no structural linking work at all.
const serviceLinks = [
  { label: 'AI Agents & Agentic Workflows', to: '/#services' },
  { label: 'AFK Development', to: '/afk-development' },
  { label: 'AWS Cloud Infrastructure', to: '/#aws' },
];

const companyLinks = [
  { label: 'About', to: '/about' },
  { label: 'Why Kult.io', to: '/#why' },
  { label: 'Case studies', to: '/#projects' },
  { label: 'Blog', to: '/blog' },
  { label: 'Contact', to: '/contact' },
];

const Footer: React.FC = () => {
  return (
    <footer className="bg-gray-900 border-t border-gray-800">
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center">
              <Link to="/" className="inline-flex items-center min-h-[44px]">
                 <img className="h-8 w-auto" src={logoUrl} width="131" height="26" alt="Kult.io" />
              </Link>
            </div>
            <p className="mt-4 text-sm text-gray-400">
              AI agents and agentic workflows, specializing in Anthropic Claude. AWS cloud infrastructure for 10+ years.
            </p>
            {/* Social Media Icons - Placeholder for now, can be replaced with lucide-react or other SVG icons */}
            <div className="mt-6 flex space-x-4">
              <a href={LINKEDIN_COMPANY} target="_blank" rel="noopener noreferrer" className="-m-2.5 inline-flex h-11 w-11 items-center justify-center text-gray-400 transition-colors duration-150 hover:text-accent-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400">
                <span className="sr-only">LinkedIn</span>
                {/* Placeholder SVG - replace with actual icon */}
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path fillRule="evenodd" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" clipRule="evenodd" /></svg>
              </a>
              {/* Repeat for other social icons (Twitter, GitHub) with appropriate SVGs */}
            </div>
          </div>
          <div>
            <h3 className="font-mono text-eyebrow uppercase text-gray-400">Services</h3>
            <ul className={LIST_CLASS}>
              {serviceLinks.map((link) => (
                <li key={link.label}>
                  <Link to={link.to} className={LINK_CLASS}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-mono text-eyebrow uppercase text-gray-400">Company</h3>
            <ul className={LIST_CLASS}>
              {companyLinks.map((link) => (
                <li key={link.label}>
                  <Link to={link.to} className={LINK_CLASS}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-mono text-eyebrow uppercase text-gray-400">Contact Us</h3>
            <address className="mt-4 space-y-3 text-base text-gray-400 not-italic">
              <p>Aleja Powstania Warszawskiego 15</p>
              <p>31-539 Kraków</p>
              <p>Poland</p>
              <p><a href={`mailto:${CONTACT_EMAIL}`} className={LINK_CLASS}>{CONTACT_EMAIL}</a></p>
            </address>
          </div>
        </div>
        <div className="mt-12 border-t border-gray-700 pt-8">
          {/* The one render-time value on the page. Without this, crossing midnight on
              31 December would make a prerendered year mismatch and force a full
              client re-render of the whole tree. */}
          <p className="text-base text-gray-400 text-center" suppressHydrationWarning>
            © {new Date().getFullYear()} Kult.io. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
